import { useState, useEffect } from 'react'
import { QuizCard } from './quiz-card'
import { fetchQuizQuestions, saveUserPreferences } from '@/lib/quiz'
import type { QuizQuestion, UserTraits } from '@/types/quiz'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth'

export function QuizManager() {
  const [questions, setQuestions] = useState<QuizQuestion[]>([])
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [userTraits, setUserTraits] = useState<UserTraits>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [completed, setCompleted] = useState(false)
  const { user } = useAuth()

  useEffect(() => {
    loadQuestions()
  }, [])

  async function loadQuestions() {
    setLoading(true)
    setError(null)
    try {
      const quizQuestions = await fetchQuizQuestions()
      if (!quizQuestions?.length) {
        throw new Error('No questions available')
      }
      setQuestions(quizQuestions)
    } catch (error) {
      console.error('Failed to load questions:', error)
      setError('Failed to load quiz questions. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  const handleSelect = async (selectedTraits: string[]) => {
    const category = questions[currentQuestion].category
    const updatedTraits = {
      ...userTraits,
      [category]: {
        ...(userTraits[category] || {}),
        ...selectedTraits.reduce((acc, trait) => ({
          ...acc,
          [trait]: ((userTraits[category] || {})[trait] || 0) + 1
        }), {})
      }
    }
    setUserTraits(updatedTraits)

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1)
    } else {
      try {
        if (user) {
          await saveUserPreferences(user.id, updatedTraits)
        }
        setCompleted(true)
      } catch (error) {
        console.error('Failed to save preferences:', error)
      }
    }
  }

  const handleRetake = () => {
    setCurrentQuestion(0)
    setUserTraits({})
    setCompleted(false)
  }

  if (loading) {
    return (
      <div className="text-center">
        <p className="text-muted-foreground">Loading questions...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4 text-center">
        <p className="text-destructive">{error}</p>
        <Button onClick={loadQuestions}>Try Again</Button>
      </div>
    )
  }

  if (!questions.length) {
    return (
      <div className="text-center">
        <p className="text-muted-foreground">No questions available.</p>
      </div>
    )
  }

  if (completed) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold">Thank You!</h2>
        <p className="text-muted-foreground">
          Your preferences have been saved. We'll use this information to enhance
          your travel recommendations.
        </p>
        <Button onClick={handleRetake}>Retake Quiz</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold">Travel Preference Quiz</h2>
        <p className="text-muted-foreground">
          Help us understand your travel style to provide better recommendations.<br />
          Question {currentQuestion + 1} of {questions.length}
        </p>
      </div>

      <QuizCard
        question={questions[currentQuestion]}
        onSelect={handleSelect}
      />
    </div>
  )
}